import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { isNumeric, createCustomPropType } from '../../../../../js/app/react/validators';
import SimpleSelect from '../../../SimpleSelect/SimpleSelectView';
import AdvForm from '../../../../CommonComponents/AdvForm';
import InputDollar from '../../../InputDollar/InputDollarView';
import InputPercentage from '../../../InputPercentage/InputPercentageView';
import Input, { LEFT } from '../../../Input/InputView';
import Validation from '../../../Validation/ValidationContainer';
import ButtonWithLoading from '../../../ButtonWithLoading/ButtonWithLoadingContainer';
import { BACKEND_KEY } from '../../AccountsWorkflowConstants';
import DateInput from '../../../DateInput/DateInputView';
import BackMobileNavigation from '../../../BackMobileNavigation/BackMobileNavigationView';
import { AccountOwnership } from '../../../AccountsWorkflow/components/AccountOwnership/AccountOwnership';
import { Alert } from '../../../../CommonComponents/OPL/Alert/Alert';
import { useOwnershipValidation } from '../../../AccountsWorkflow/components/AccountOwnership/useOwnershipValidation';

const AccountAddEditView = ({
  workflowId,
  clientId,
  onCancel,
  onSave,
  accountInputChanged,
  accountSimpleSelectChanged,
  accountTypeChanged,
  accountDateChanged,
  graphAccounts,
  groups,

  account,

  accountTypeOption,
  accountTypesOptions,

  accountOwnerOption,
  accountOwnersOptions,
  accountOwnersGroups,

  mortgageTypeOption,
  mortgageTypesOptions,

  interestRateLabel,
  isMortgageAccount,
  isStudentLoanAccount,

  accountTypes,
  updateDraftAccount,

  ownerModel,
}) => {
  const [isOwnershipError, setIsOwnershipError] = useState(false);
  return (
    <AdvForm className="AccountAddEditView with-mobile-top-padding">
      <BackMobileNavigation
        label="Add Debt"
        onBack={onCancel(workflowId, clientId, account, graphAccounts)}
      />
      <Validation workflowId={workflowId} validationId={`${BACKEND_KEY}.AccountAddEdit`} />
      {isOwnershipError && (
        <Alert
          mt="16"
          mb="16"
          message={'The total ownership percentage must add up to 100%'}
          variant="error"
        />
      )}
      <h2 className="h2">Please add your debt information.</h2>
      <ul className="form-input-list">
        <li className="form-input">
          <SimpleSelect
            placeholder="Account Type"
            id="adv-account-type"
            className="no-horizontal-scroll sm"
            value={accountTypeOption}
            options={accountTypesOptions}
            isLeftAligned
            groups={groups}
            onValueChange={accountTypeChanged(workflowId, clientId)}
          />
        </li>
        {account.AccountType !== undefined && (
          <AccountOwnership
            selectedAccountType={account.AccountType}
            ownerModel={ownerModel}
            updateAccount={data => updateDraftAccount(data, workflowId, clientId)}
            accountTypes={accountTypes}
            clientId={clientId}
          />
        )}
        {isMortgageAccount && (
          <li className="form-input">
            <SimpleSelect
              placeholder="Mortgage Type"
              id="adv-mortgage-type"
              className="no-horizontal-scroll sm"
              isLeftAligned
              value={mortgageTypeOption}
              options={mortgageTypesOptions}
              onValueChange={accountSimpleSelectChanged('MortgageType', workflowId, clientId)}
            />
          </li>
        )}
        <li className="form-input">
          <InputDollar
            maxLength="256"
            placeholder="Balance"
            id="adv-balance"
            className="accounts"
            isLeftAligned
            value={account.NonSpecificPositionsValue}
            onChange={accountInputChanged('NonSpecificPositionsValue', workflowId, clientId)}
          />
        </li>
        <li className="form-input">
          <InputPercentage
            placeholder={interestRateLabel}
            id="adv-interest-rate"
            maxLength="256"
            className="accounts"
            isLeftAligned
            value={account.InterestRate}
            onBlur={accountInputChanged('InterestRate', workflowId, clientId)}
            onChange={accountInputChanged('InterestRate', workflowId, clientId)}
            decimalScale={3}
          />
        </li>
        {!isMortgageAccount && (
          <li className="form-input">
            <Input
              type="text"
              maxLength="256"
              alignment={LEFT}
              id="adv-account-description"
              placeholder="Account Description"
              className="nickname"
              value={account.AccountName}
              readOnly={account.IsOrion}
              onChange={accountInputChanged('AccountName', workflowId, clientId)}
            />
          </li>
        )}
        <li className="form-input">
          <InputDollar
            maxLength="256"
            placeholder="Monthly Payment"
            id="adv-monthly-payment"
            className="accounts"
            isLeftAligned
            value={account.MonthlyContribution}
            onChange={accountInputChanged('MonthlyContribution', workflowId, clientId)}
          />
        </li>
        {(isMortgageAccount || isStudentLoanAccount) &&
          (() => {
            const fieldName = isMortgageAccount ? 'OriginationDate' : 'StudentLoanOriginationDate';
            return (
              <li className="form-input has-calendar">
                <DateInput
                  placeholder="Origination Date"
                  value={account[fieldName]}
                  onChange={accountDateChanged(fieldName, workflowId, clientId)}
                  maxDate={isStudentLoanAccount ? new Date() : undefined}
                  id="adv-origination-date"
                />
              </li>
            );
          })()}
      </ul>
      <div className="footer">
        <button
          type="button"
          className="adv-workflow-navigation-cancel-button"
          onClick={onCancel(workflowId, clientId, account, graphAccounts)}
        >
          Cancel
        </button>
        <ButtonWithLoading
          workflowId={workflowId}
          type="button"
          className="adv-workflow-navigation-next-save-button"
          onClick={() => {
            const validation = useOwnershipValidation(account.OwnerModel);
            setIsOwnershipError(!validation);
            if (validation) {
              onSave(
                {
                  ...account,
                  OwnerModel: account.OwnerModel?.filter(b => b.Name && b.percent).map(owner => ({
                    ...owner,
                    Percentage: Number((parseFloat(owner.percent ?? '0') / 100).toFixed(4)),
                  })),
                },
                graphAccounts,
                workflowId,
                clientId,
                accountTypeOption,
              );
            }
          }}
        >
          Save Debt
        </ButtonWithLoading>
      </div>
    </AdvForm>
  );
};

AccountAddEditView.defaultProps = {
  accountTypeOption: null,
  accountOwnerOption: null,
  mortgageTypeOption: null,
};

AccountAddEditView.propTypes = {
  clientId: createCustomPropType(isNumeric).isRequired,
  workflowId: PropTypes.string.isRequired,
  onCancel: PropTypes.func.isRequired,
  isMortgageAccount: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  accountSimpleSelectChanged: PropTypes.func.isRequired,
  accountInputChanged: PropTypes.func.isRequired,
  accountTypeChanged: PropTypes.func.isRequired,
  accountDateChanged: PropTypes.func.isRequired,

  accountTypeOption: PropTypes.number,
  mortgageTypeOption: PropTypes.number,

  accountOwnersGroups: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  accountTypesOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  mortgageTypesOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,

  accountOwnerOption: PropTypes.shape({}),
  accountOwnersOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,

  account: PropTypes.shape({}).isRequired,
  graphAccounts: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  groups: PropTypes.arrayOf(PropTypes.shape({})).isRequired,

  interestRateLabel: PropTypes.string.isRequired,
  isStudentLoanAccount: PropTypes.bool.isRequired,
};

export default AccountAddEditView;
